import React, { useRef, useState } from "react";
import { CheckCircle2, Circle, Download, Trash2, Upload, Pencil, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const download = async (pack, f) => {
  try {
    const r = await api.get(`/job-packs/${pack.id}/files/${f.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = f.filename; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not download the file"); }
};

const Checklist = ({ pack }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`jp-checklist-${pack.id}`}>
    {pack.checklist.map((c) => (
      <span key={c.category} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${c.present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
        {c.present ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {c.category}{c.present ? "" : " missing"}
      </span>
    ))}
  </div>
);

const Uploader = ({ pack, categories, onChanged }) => {
  const [cat, setCat] = useState(categories[0] || "Quote");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (files) => {
    setBusy(true);
    let latest = null;
    for (const file of files) {
      const fd = new FormData();
      fd.append("category", cat); fd.append("file", file);
      try { latest = (await api.post(`/job-packs/${pack.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data; }
      catch (e) { toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`); }
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
    if (latest) { toast.success("Uploaded"); onChanged(latest); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={cat} onValueChange={setCat}>
        <SelectTrigger className="h-8 w-[190px] text-xs" data-testid={`jp-category-select-${pack.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <input ref={ref} type="file" multiple className="hidden" onChange={(e) => e.target.files?.length && upload(Array.from(e.target.files))} data-testid={`jp-file-input-${pack.id}`} />
      <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => ref.current?.click()} data-testid={`jp-upload-button-${pack.id}`}>
        <Upload className="h-3.5 w-3.5 mr-1" /> {busy ? "Uploading..." : "Upload files"}
      </Button>
    </div>
  );
};

export const JobPackCard = ({ pack, categories, readOnly, onChanged, onEdit, onDelete }) => {
  const removeFile = async (f) => {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    try { onChanged((await api.delete(`/job-packs/${pack.id}/files/${f.id}`)).data); toast.success("File removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div data-testid={`jp-card-${pack.id}`} className="glass-card rounded-2xl p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-[15px]">{pack.job_number} {pack.customer && <span className="text-slate-500 font-medium">· {pack.customer}</span>}</div>
          <div className="text-[11px] text-slate-500">{fmt(pack.job_date)} · {pack.files.length} file{pack.files.length === 1 ? "" : "s"}{pack.description ? ` · ${pack.description}` : ""}</div>
        </div>
        <Badge variant="outline" className={`ml-auto rounded-full text-[10px] ${pack.complete ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`jp-status-${pack.id}`}>
          {pack.complete ? "Audit ready" : "Incomplete"}
        </Badge>
        {!readOnly && <>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} aria-label="Edit job pack" data-testid={`jp-edit-${pack.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={onDelete} aria-label="Delete job pack" data-testid={`jp-delete-${pack.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </>}
      </div>
      <Checklist pack={pack} />
      {pack.files.length > 0 && (
        <div className="divide-y divide-slate-100 dark:divide-white/5 rounded-xl border border-slate-200/70 dark:border-white/10">
          {pack.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`jp-file-${f.id}`}>
              <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 w-[110px] shrink-0 truncate">{f.category}</span>
              <span className="truncate flex-1">{f.filename}</span>
              <span className="text-[11px] text-slate-400 hidden sm:inline">{size(f.size)} · {f.uploaded_by}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(pack, f)} aria-label="Download" data-testid={`jp-download-${f.id}`}><Download className="h-3.5 w-3.5" /></Button>
              {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => removeFile(f)} aria-label="Remove file" data-testid={`jp-remove-file-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          ))}
        </div>
      )}
      {!readOnly && <Uploader pack={pack} categories={categories} onChanged={onChanged} />}
    </div>
  );
};
